export const SAVE_STORAGE_KEY = 'slimePang.save.v1';
export const CURRENT_SCHEMA = 1;

export interface SaveSettings {
  muted: boolean;
  vibrationEnabled: boolean;
  masterVolume: number;
}

export interface SaveCurrency {
  gold: number;
  lastGoldAdWatchTime: number;
  totalGoldEarned: number;
  totalGoldSpent: number;
  scoreBoostLevel: number;
  comboShields: number;
  shieldsUsedTotal: number;
  upgradesPurchased: number;
}

export interface SaveBonus {
  availableTokens: number;
  isBonusActive: boolean;
  isUnlocked: boolean;
  lastAdWatchTime: number;
  totalTokensEarned: number;
  totalTokensUsed: number;
}

export interface SaveDaily {
  currentStreak: number;
  lastLoginDate: string;
  lastRewardClaimedDate: string;
  totalDaysPlayed: number;
  longestStreak: number;
}

export interface SavePersonalBest {
  score: number;
  maxCombo: number;
  achievedAt: number;
}

export interface PlayerSave {
  schema: number;
  playerId: string;
  username: string | null;
  settings: SaveSettings;
  currency: SaveCurrency;
  bonus: SaveBonus;
  daily: SaveDaily;
  pb: SavePersonalBest;
}

export const DEFAULT_SETTINGS: SaveSettings = {
  muted: false,
  vibrationEnabled: false,
  masterVolume: 1,
};

export const DEFAULT_CURRENCY: SaveCurrency = {
  gold: 0,
  lastGoldAdWatchTime: 0,
  totalGoldEarned: 0,
  totalGoldSpent: 0,
  scoreBoostLevel: 0,
  comboShields: 0,
  shieldsUsedTotal: 0,
  upgradesPurchased: 0,
};

export const DEFAULT_BONUS: SaveBonus = {
  availableTokens: 0,
  isBonusActive: false,
  isUnlocked: false,
  lastAdWatchTime: 0,
  totalTokensEarned: 0,
  totalTokensUsed: 0,
};

export const DEFAULT_DAILY: SaveDaily = {
  currentStreak: 0,
  lastLoginDate: '',
  lastRewardClaimedDate: '',
  totalDaysPlayed: 0,
  longestStreak: 0,
};

export const DEFAULT_PB: SavePersonalBest = {
  score: 0,
  maxCombo: 0,
  achievedAt: 0,
};

export type Migration = (state: Record<string, unknown>) => Record<string, unknown>;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type CaptureError = (error: unknown, context?: string) => void;

export interface PlayerStoreOptions {
  storage?: StorageLike | null;
  captureError?: CaptureError;
  generateId?: () => string;
  schemaVersion?: number;
  migrations?: Record<number, Migration>;
}

export interface PlayerStore {
  get(): PlayerSave;
  patchSettings(partial: Partial<SaveSettings>): PlayerSave;
  setUsername(username: string | null): PlayerSave;
  setPersonalBest(pb: Partial<SavePersonalBest>): PlayerSave;
  setCurrency(currency: SaveCurrency): PlayerSave;
  setBonus(bonus: SaveBonus): PlayerSave;
  setDaily(daily: SaveDaily): PlayerSave;
  resetAll(): PlayerSave;
  subscribe(listener: (state: PlayerSave) => void): () => void;
}

export function calculateChecksum(state: unknown): string {
  const dataString = JSON.stringify(state);
  let checksum = 0;
  for (let i = 0; i < dataString.length; i++) {
    checksum += dataString.charCodeAt(i);
  }
  return checksum.toString(16);
}

export function migrate(
  state: Record<string, unknown>,
  fromSchema: number,
  toSchema: number,
  migrations: Record<number, Migration>,
): Record<string, unknown> {
  let current = state;
  for (let v = fromSchema; v < toSchema; v++) {
    const step = migrations[v];
    if (!step) {
      throw new Error(`missing migration from schema ${v} to ${v + 1}`);
    }
    current = step(current);
  }
  return current;
}

function buildDefaults(playerId: string, schemaVersion: number): PlayerSave {
  return {
    schema: schemaVersion,
    playerId,
    username: null,
    settings: { ...DEFAULT_SETTINGS },
    currency: { ...DEFAULT_CURRENCY },
    bonus: { ...DEFAULT_BONUS },
    daily: { ...DEFAULT_DAILY },
    pb: { ...DEFAULT_PB },
  };
}

function defaultGenerateId(): string {
  return crypto.randomUUID();
}

function safeStorage(): StorageLike | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

interface LoadedSave {
  state: PlayerSave;
  freshlyGenerated: boolean;
  migrated: boolean;
}

interface LoadContext {
  storage: StorageLike | null;
  captureError: CaptureError;
  generateId: () => string;
  schemaVersion: number;
  migrations: Record<number, Migration>;
}

function load(ctx: LoadContext): LoadedSave {
  const fresh = (): LoadedSave => ({
    state: buildDefaults(ctx.generateId(), ctx.schemaVersion),
    freshlyGenerated: true,
    migrated: false,
  });
  if (!ctx.storage) return fresh();
  let raw: string | null;
  try {
    raw = ctx.storage.getItem(SAVE_STORAGE_KEY);
  } catch (error) {
    ctx.captureError(error, 'playerStore.load.getItem');
    return fresh();
  }
  if (!raw) return fresh();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    ctx.captureError(error, 'playerStore.load.parse');
    return fresh();
  }
  if (!parsed || typeof parsed !== 'object') {
    ctx.captureError(new Error('save not an object'), 'playerStore.load.shape');
    return fresh();
  }
  const obj = parsed as Record<string, unknown>;
  const checksum = obj.checksum;
  if (typeof checksum !== 'string') {
    ctx.captureError(new Error('save missing checksum'), 'playerStore.load.shape');
    return fresh();
  }
  const { checksum: _omitted, ...withoutChecksum } = obj;
  if (calculateChecksum(withoutChecksum) !== checksum) {
    ctx.captureError(new Error('save checksum mismatch'), 'playerStore.load.checksum');
    return fresh();
  }
  const storedSchema = withoutChecksum.schema;
  if (typeof storedSchema !== 'number' || !Number.isFinite(storedSchema)) {
    ctx.captureError(new Error('save missing schema'), 'playerStore.load.shape');
    return fresh();
  }
  let migrated: Record<string, unknown>;
  if (storedSchema === ctx.schemaVersion) {
    migrated = withoutChecksum;
  } else if (storedSchema < ctx.schemaVersion) {
    try {
      migrated = migrate(withoutChecksum, storedSchema, ctx.schemaVersion, ctx.migrations);
    } catch (error) {
      ctx.captureError(error, 'playerStore.load.migrate');
      return fresh();
    }
  } else {
    ctx.captureError(
      new Error(`save schema ${storedSchema} > current ${ctx.schemaVersion}`),
      'playerStore.load.future-schema',
    );
    return fresh();
  }
  let candidate: PlayerSave;
  try {
    candidate = coerce(migrated, ctx.schemaVersion);
  } catch (error) {
    ctx.captureError(error, 'playerStore.load.coerce');
    return fresh();
  }
  return {
    state: candidate,
    freshlyGenerated: false,
    migrated: storedSchema !== ctx.schemaVersion,
  };
}

function coerce(input: Record<string, unknown>, schemaVersion: number): PlayerSave {
  const playerId = input.playerId;
  if (typeof playerId !== 'string' || playerId.length === 0) {
    throw new Error('save missing playerId');
  }
  const username = input.username;
  if (username !== null && typeof username !== 'string') {
    throw new Error('save username invalid');
  }
  return {
    schema: schemaVersion,
    playerId,
    username,
    settings: coerceSettings(input.settings),
    currency: coerceCurrency(input.currency),
    bonus: coerceBonus(input.bonus),
    daily: coerceDaily(input.daily),
    pb: coercePb(input.pb),
  };
}

function coerceSettings(input: unknown): SaveSettings {
  const obj = (input ?? {}) as Record<string, unknown>;
  const rawVolume = obj.masterVolume;
  const masterVolume =
    typeof rawVolume === 'number' && Number.isFinite(rawVolume)
      ? Math.min(1, Math.max(0, rawVolume))
      : DEFAULT_SETTINGS.masterVolume;
  return {
    muted: typeof obj.muted === 'boolean' ? obj.muted : DEFAULT_SETTINGS.muted,
    vibrationEnabled:
      typeof obj.vibrationEnabled === 'boolean'
        ? obj.vibrationEnabled
        : DEFAULT_SETTINGS.vibrationEnabled,
    masterVolume,
  };
}

function coerceCurrency(input: unknown): SaveCurrency {
  const obj = (input ?? {}) as Record<string, unknown>;
  return {
    gold: numField(obj.gold, DEFAULT_CURRENCY.gold),
    lastGoldAdWatchTime: numField(obj.lastGoldAdWatchTime, DEFAULT_CURRENCY.lastGoldAdWatchTime),
    totalGoldEarned: numField(obj.totalGoldEarned, DEFAULT_CURRENCY.totalGoldEarned),
    totalGoldSpent: numField(obj.totalGoldSpent, DEFAULT_CURRENCY.totalGoldSpent),
    scoreBoostLevel: numField(obj.scoreBoostLevel, DEFAULT_CURRENCY.scoreBoostLevel),
    comboShields: numField(obj.comboShields, DEFAULT_CURRENCY.comboShields),
    shieldsUsedTotal: numField(obj.shieldsUsedTotal, DEFAULT_CURRENCY.shieldsUsedTotal),
    upgradesPurchased: numField(obj.upgradesPurchased, DEFAULT_CURRENCY.upgradesPurchased),
  };
}

function coerceBonus(input: unknown): SaveBonus {
  const obj = (input ?? {}) as Record<string, unknown>;
  return {
    availableTokens: numField(obj.availableTokens, DEFAULT_BONUS.availableTokens),
    isBonusActive:
      typeof obj.isBonusActive === 'boolean' ? obj.isBonusActive : DEFAULT_BONUS.isBonusActive,
    isUnlocked: typeof obj.isUnlocked === 'boolean' ? obj.isUnlocked : DEFAULT_BONUS.isUnlocked,
    lastAdWatchTime: numField(obj.lastAdWatchTime, DEFAULT_BONUS.lastAdWatchTime),
    totalTokensEarned: numField(obj.totalTokensEarned, DEFAULT_BONUS.totalTokensEarned),
    totalTokensUsed: numField(obj.totalTokensUsed, DEFAULT_BONUS.totalTokensUsed),
  };
}

function coerceDaily(input: unknown): SaveDaily {
  const obj = (input ?? {}) as Record<string, unknown>;
  return {
    currentStreak: numField(obj.currentStreak, DEFAULT_DAILY.currentStreak),
    lastLoginDate:
      typeof obj.lastLoginDate === 'string' ? obj.lastLoginDate : DEFAULT_DAILY.lastLoginDate,
    lastRewardClaimedDate:
      typeof obj.lastRewardClaimedDate === 'string'
        ? obj.lastRewardClaimedDate
        : DEFAULT_DAILY.lastRewardClaimedDate,
    totalDaysPlayed: numField(obj.totalDaysPlayed, DEFAULT_DAILY.totalDaysPlayed),
    longestStreak: numField(obj.longestStreak, DEFAULT_DAILY.longestStreak),
  };
}

function coercePb(input: unknown): SavePersonalBest {
  const obj = (input ?? {}) as Record<string, unknown>;
  return {
    score: numField(obj.score, DEFAULT_PB.score),
    maxCombo: numField(obj.maxCombo, DEFAULT_PB.maxCombo),
    achievedAt: numField(obj.achievedAt, DEFAULT_PB.achievedAt),
  };
}

function numField(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function createPlayerStore(options: PlayerStoreOptions = {}): PlayerStore {
  const ctx: LoadContext = {
    storage: options.storage === undefined ? safeStorage() : options.storage,
    captureError: options.captureError ?? (() => undefined),
    generateId: options.generateId ?? defaultGenerateId,
    schemaVersion: options.schemaVersion ?? CURRENT_SCHEMA,
    migrations: options.migrations ?? {},
  };
  const initial = load(ctx);
  let state: PlayerSave = initial.state;
  const listeners = new Set<(state: PlayerSave) => void>();

  const persist = (next: PlayerSave): void => {
    if (!ctx.storage) return;
    try {
      const checksum = calculateChecksum(next);
      ctx.storage.setItem(SAVE_STORAGE_KEY, JSON.stringify({ ...next, checksum }));
    } catch (error) {
      ctx.captureError(error, 'playerStore.persist');
    }
  };

  if (initial.freshlyGenerated || initial.migrated) {
    persist(initial.state);
  }

  const apply = (next: PlayerSave): PlayerSave => {
    state = next;
    persist(next);
    for (const listener of listeners) listener(next);
    return next;
  };

  return {
    get: () => state,
    patchSettings(partial) {
      const merged = { ...state.settings, ...partial };
      const settings: SaveSettings = {
        muted: merged.muted,
        vibrationEnabled: merged.vibrationEnabled,
        masterVolume: Math.min(1, Math.max(0, merged.masterVolume)),
      };
      if (
        settings.muted === state.settings.muted &&
        settings.vibrationEnabled === state.settings.vibrationEnabled &&
        settings.masterVolume === state.settings.masterVolume
      ) {
        return state;
      }
      return apply({ ...state, settings });
    },
    setUsername(username) {
      if (state.username === username) return state;
      return apply({ ...state, username });
    },
    setPersonalBest(pb) {
      const next: SavePersonalBest = { ...state.pb, ...pb };
      if (
        next.score === state.pb.score &&
        next.maxCombo === state.pb.maxCombo &&
        next.achievedAt === state.pb.achievedAt
      ) {
        return state;
      }
      return apply({ ...state, pb: next });
    },
    setCurrency(currency) {
      return apply({ ...state, currency });
    },
    setBonus(bonus) {
      return apply({ ...state, bonus });
    },
    setDaily(daily) {
      return apply({ ...state, daily });
    },
    resetAll() {
      const next = buildDefaults(ctx.generateId(), ctx.schemaVersion);
      return apply(next);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
